import React, { useEffect, useRef, useState } from "react";
import { Table, Spinner } from "reactstrap";

/**
 * Resident list — Unit Number, Name, Contact Number, Email, and the unit-level
 * Garage Code / Garbage Bin / Composite Bin columns.
 *
 * People are boxed per unit (per tenancy window): lease holders first, then
 * occupants, each labelled and styled so the two are never mistaken for one
 * another. Contact Number and Email are editable in place for CURRENT residents
 * and save to the Resident Directory's own store on blur or Enter. Past
 * residents come from archived items and historic snapshots, which are records
 * of what was true at the time, so their contact is shown read-only.
 */
const cell = (value) => (String(value ?? "").trim() ? String(value).trim() : "—");

const rowKey = (row, i) => `${row.buildingName}-${row.unitNo}-${row.moveIn}-${i}`;

/** Current tenancies can be edited; edits save to the Resident Directory store. */
export const isRowEditable = (row) =>
  row?.status === "current" && (row?.source === "active" || row?.source === "directory");

/**
 * One box per unit per tenancy window. Occupants belong to a tenancy, so a
 * unit's past and current tenancies stay in separate boxes rather than being
 * mixed into one household that never existed.
 */
export const groupRowsByUnit = (rows) => {
  const groups = [];
  const index = new Map();
  (rows || []).forEach((row) => {
    const key =
      row.status === "current"
        ? [row.buildingName ?? "", row.unitNo, "current"].join("|")
        : [row.buildingName ?? "", row.unitNo, row.moveIn ?? "", row.moveOut ?? ""].join("|");
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, rows: [] });
    }
    groups[index.get(key)].rows.push(row);
  });
  for (const group of groups) {
    group.rows.sort((a, b) => {
      if (a.role !== b.role) return a.role === "occupant" ? 1 : -1;
      return String(a.name).localeCompare(String(b.name));
    });
  }
  return groups;
};

const EditableCell = ({ row, field, type, placeholder, label, onSave }) => {
  const original = row[field] ?? "";
  const [value, setValue] = useState(original);
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  const inputRef = useRef(null);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValue(row[field] ?? "");
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = async () => {
    const next = String(value ?? "").trim();
    if (next === String(original ?? "").trim()) return;
    setState("saving");
    try {
      await onSave(next);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setState("error");
      setValue(original ?? ""); // put the old value back so nothing looks saved
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      setValue(original ?? "");
      setState("idle");
      inputRef.current?.blur();
    }
  };

  const border =
    state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : "transparent";

  return (
    <span className="rd-edit">
      <input
        ref={inputRef}
        type={type}
        className="rd-edit-input"
        style={{ borderColor: border }}
        value={value}
        placeholder={placeholder}
        aria-label={`${label} for ${row.name || `unit ${row.unitNo}`}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        disabled={state === "saving"}
      />
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the Resident Directory" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
    </span>
  );
};

const UNIT_FIELDS = [
  { field: "garageCode", patchKey: "garage_code", label: "Garage code", header: "Garage Code" },
  { field: "garbageBin", patchKey: "garbage_bin", label: "Garbage bin", header: "Garbage Bin" },
  { field: "compositeBin", patchKey: "composite_bin", label: "Composite bin", header: "Composite Bin" },
];

const ResidentTable = ({ rows, onSaveContact, onSaveUnit }) => (
  <div className="rd-table-wrap">
    <Table responsive className="rd-table mb-0">
      <thead>
        <tr>
          <th scope="col">Unit Number</th>
          <th scope="col">Name</th>
          <th scope="col">Contact Number</th>
          <th scope="col">Email</th>
          {UNIT_FIELDS.map((f) => (
            <th scope="col" key={f.field}>{f.header}</th>
          ))}
        </tr>
      </thead>
      {groupRowsByUnit(rows).map((group) => {
        const firstRow = group.rows[0];
        const unitEditable =
          group.rows.some((r) => isRowEditable(r)) && typeof onSaveUnit === "function";
        return (
          <tbody className="rd-unit-group" key={group.key}>
            {group.rows.map((row, i) => {
              const editable = isRowEditable(row) && typeof onSaveContact === "function";
              const nameEditable =
                row.source === "directory" && editable && typeof onSaveUnit === "function";
              const isOccupant = row.role === "occupant";
              return (
                <tr key={rowKey(row, i)} className={isOccupant ? "rd-row-occupant" : "rd-row-tenant"}>
                  {i === 0 && (
                    <td className="rd-unit-cell" rowSpan={group.rows.length}>
                      {cell(row.unitNo)}
                    </td>
                  )}
                  <td>
                    <span className="rd-person">
                      {nameEditable ? (
                        <EditableCell
                          row={row}
                          field="name"
                          type="text"
                          placeholder="Name"
                          label="Resident name"
                          onSave={(v) =>
                            onSaveUnit(row, { name_edit: { from: row.name, to: v } })
                          }
                        />
                      ) : (
                        <span className="rd-person-name">{cell(row.name)}</span>
                      )}
                      <span className={`rd-role-pill ${isOccupant ? "is-occupant" : "is-tenant"}`}>
                        {isOccupant ? "Occupant" : "Lease Holder"}
                      </span>
                    </span>
                  </td>
                  <td>
                    {editable ? (
                      <EditableCell
                        row={row}
                        field="phone"
                        type="tel"
                        placeholder="Add number"
                        label="Contact number"
                        onSave={(v) => onSaveContact(row, { phone: v })}
                      />
                    ) : (
                      cell(row.phone)
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <EditableCell
                        row={row}
                        field="email"
                        type="email"
                        placeholder="Add email"
                        label="Email"
                        onSave={(v) => onSaveContact(row, { email: v })}
                      />
                    ) : (
                      cell(row.email)
                    )}
                  </td>
                  {i === 0 &&
                    UNIT_FIELDS.map((f) => (
                      <td className="rd-unit-field-cell" rowSpan={group.rows.length} key={f.field}>
                        {unitEditable ? (
                          <EditableCell
                            row={firstRow}
                            field={f.field}
                            type="text"
                            placeholder="Add"
                            label={f.label}
                            onSave={(v) => onSaveUnit(firstRow, { [f.patchKey]: v })}
                          />
                        ) : (
                          cell(firstRow[f.field])
                        )}
                      </td>
                    ))}
                </tr>
              );
            })}
          </tbody>
        );
      })}
    </Table>
  </div>
);

export { ResidentTable };
